import React, { useState } from "react";
import { Megaphone, Wrench, ShieldCheck, ChevronDown } from "lucide-react";

import TEMPLATE_LANGUAGES from "../languages";
import { Card, Label, Hint, TextInput, Select, Segmented } from "./ui";

const CATEGORIES = [
  {
    value: "MARKETING",
    label: "Marketing",
    description: "Promotions, offers, announcements and product news.",
    icon: Megaphone,
  },
  {
    value: "UTILITY",
    label: "Utility",
    description: "Order updates, reminders and account notifications.",
    icon: Wrench,
  },
  {
    value: "AUTHENTICATION",
    label: "Authentication",
    description: "One-time passcodes for sign-in and verification.",
    icon: ShieldCheck,
  },
];

const sanitizeName = (v) =>
  v.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");

const BasicInformationCard = ({ value, onChange }) => {
  const [advanced, setAdvanced] = useState(false);

  return (
    <Card className="space-y-5">
      <div>
        <Label>Category</Label>
        <Segmented
          options={CATEGORIES}
          value={value.category}
          onChange={(category) =>
            onChange({
              category,
              // carousel only exists for marketing
              template_type: category === "MARKETING" ? value.template_type : "STANDARD",
            })
          }
          columns={1}
        />
        <Hint>
          Meta re-checks the category during review and may re-classify the
          template.
        </Hint>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label required>Template name</Label>
          <TextInput
            value={value.name}
            onChange={(e) => onChange({ name: sanitizeName(e.target.value) })}
            placeholder="order_confirmation"
            maxLength={512}
          />
          <Hint>Lowercase letters, numbers and underscores only.</Hint>
        </div>

        <div>
          <Label required>Language</Label>
          <Select
            value={value.language}
            onChange={(e) => onChange({ language: e.target.value })}
          >
            {TEMPLATE_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label} ({l.code})
              </option>
            ))}
          </Select>
          <Hint>Each language is submitted as its own template.</Hint>
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setAdvanced((v) => !v)}
          className="flex items-center gap-1.5 text-[12px] font-medium text-slate-500 hover:text-slate-700"
        >
          <ChevronDown
            size={14}
            className={`transition-transform ${advanced ? "rotate-180" : ""}`}
          />
          Advanced
        </button>

        {advanced && (
          <div className="mt-3">
            <Label hint="optional">Message time-to-live (seconds)</Label>
            <TextInput
              type="number"
              min={30}
              max={2592000}
              value={value.message_send_ttl_seconds}
              onChange={(e) =>
                onChange({ message_send_ttl_seconds: e.target.value })
              }
              placeholder="e.g. 3600"
            />
            <Hint>
              How long WhatsApp keeps trying to deliver the message. Leave blank
              for the default.
            </Hint>
          </div>
        )}
      </div>
    </Card>
  );
};

export default BasicInformationCard;
